import {Card} from "../../../basic/card.tsx";
import {Button} from "../../../basic/button.tsx";
import {Badge} from "../../../basic/badge.tsx";
import {Check, Loader2, Mail, Play, ShoppingBag} from "lucide-react";
import {useMemo, useState} from "react";
import {BillingPeriod} from "./types.ts";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {QuantitySelector} from "./QuantitySelector.tsx";
import {formatPrice} from "../../../../utils/currencyUtils";
import type {VwpPricing} from "../../../../store/generated/api.ts";
import type {PricingProductKey} from "./pricingProductCatalog.ts";

interface Product {
    key: PricingProductKey;
    label: string;
    title: string;
    description?: string;
    highlighted: boolean;
    trialCta: string;
    buyCta: string;
    featureKeys: string[];
    contactOnly?: boolean;
    showTrial?: boolean;
    hideQuantity?: boolean;
}

const DEFAULT_VISIBLE_FEATURES = 5;

const getProductTranslationKey = (productKey: PricingProductKey): string => {
    if (productKey === 'education') return 'educational';
    if (productKey === 'education_student') return 'educationalStudent';
    return productKey;
};

interface PricingCardProps {
    product: Product;
    billingPeriod: BillingPeriod;
    pricingData?: VwpPricing;
    onTrialClick: (product: Product) => void;
    onBuyClick: (product: Product) => void;
    onContactClick: () => void;
    isLoading: boolean;
    quantity: number;
    onQuantityChange: (quantity: number) => void;
    userCurrency: string;
    userLocale?: string;
    hasEducationLicense?: boolean;
}

export function PricingCard({
                                product,
                                billingPeriod,
                                pricingData,
                                onTrialClick,
                                onBuyClick,
                                onContactClick,
                                isLoading,
                                quantity,
                                onQuantityChange,
                                userCurrency,
                                userLocale,
                                hasEducationLicense
                            }: PricingCardProps) {
    const {t} = useTranslationWithKeys();
    const [showAllFeatures, setShowAllFeatures] = useState(false);

    const translatedFeatures = useMemo(() => {
        const productTranslationKey = getProductTranslationKey(product.key);
        return product.featureKeys.map(featureKey => t(`pricing.products.${productTranslationKey}.features.${featureKey}`));
    }, [product.key, product.featureKeys, t]);

    const hasMoreFeatures = translatedFeatures.length > DEFAULT_VISIBLE_FEATURES;
    const hiddenFeaturesCount = Math.max(0, translatedFeatures.length - DEFAULT_VISIBLE_FEATURES);
    const visibleFeatures = showAllFeatures ? translatedFeatures : translatedFeatures.slice(0, DEFAULT_VISIBLE_FEATURES);

    const calculateTieredPrice = (price: VwpPricing['purchasePrice'] | VwpPricing['supportPrice'] | null | undefined, qty: number): number => {
        if (!price) return 0;
        
        if (price.tiers && price.tiers.length > 0) {
            const sortedTiers = [...price.tiers].sort((a, b) => (a.upTo || Infinity) - (b.upTo || Infinity));
            const tier = sortedTiers.find(t => !t.upTo || qty <= t.upTo);
            if (tier) {
                return (tier.unitAmount || 0) * qty + (tier.flatAmount || 0);
            }
        }
        return (price.amount || 0) * qty;
    };

    const getPurchasePrice = (): string => {
        const price = pricingData?.isFirstPurchase
            ? pricingData?.purchasePrice
            : pricingData?.discountedPurchasePrice || pricingData?.purchasePrice;

        if (!price) return '-';
        const total = calculateTieredPrice(price, quantity);
        return formatPrice(total / 100, price.currency || userCurrency, userLocale);
    };

    const getSubscriptionPrice = (): { price: string; isAnnual: boolean } => {
        const price = pricingData?.isFirstPurchase
            ? pricingData?.supportPrice
            : pricingData?.supportPriceDiscounted || pricingData?.supportPrice;

        if (!price) return {price: '-', isAnnual: false};

        const total = calculateTieredPrice(price, quantity);
        const isAnnual = price.interval === 'year' || price.interval === 'yearly';
        const monthlyPrice = isAnnual ? Math.round(total / 12) : total;
        
        return {
            price: formatPrice(monthlyPrice / 100, price.currency || userCurrency, userLocale),
            isAnnual
        };
    };

    const getMonthlyFirstYearPrice = (): string => {
        const price = pricingData?.isFirstPurchase
            ? pricingData?.purchasePrice
            : pricingData?.discountedPurchasePrice || pricingData?.purchasePrice;

        if (!price) return '-';
        const total = calculateTieredPrice(price, quantity);
        const monthlyPrice = Math.round(total / 12);
        return formatPrice(monthlyPrice / 100, price.currency || userCurrency, userLocale);
    };

    const getMonthlySubsequentYearPrice = (): string => {
        const price = pricingData?.isFirstPurchase
            ? pricingData?.supportPrice
            : pricingData?.supportPriceDiscounted || pricingData?.supportPrice;

        if (!price) return '-';
        const total = calculateTieredPrice(price, quantity);
        const monthlyPrice = Math.round(total / 12);
        return formatPrice(monthlyPrice / 100, price.currency || userCurrency, userLocale);
    };

    const getStudentMonthlyPrice = (): string => {
        const price = pricingData?.isFirstPurchase
            ? pricingData?.purchasePrice
            : pricingData?.discountedPurchasePrice || pricingData?.purchasePrice;

        if (!price) return '-';
        const total = calculateTieredPrice(price, quantity);
        const monthlyPrice = Math.round(total / 6);
        return formatPrice(monthlyPrice / 100, price.currency || userCurrency, userLocale);
    };

    const purchasePrice = getPurchasePrice();
    const {price: subscriptionPrice, isAnnual} = getSubscriptionPrice();
    const hasSubscription = pricingData?.supportPrice && pricingData.supportPrice.amount !== undefined;

    return (
        <Card
            className={`p-6 shadow-sm border-border/50 flex flex-col ${
                product.highlighted ? 'bg-primary/5 border-primary/20' : ''
            }`}
        >
            <div className="mb-4 flex-0">
                <Badge variant="secondary" className="rounded-full">
                    {product.title}
                </Badge>
            </div>

            <h2 className="mb-4">{product.label}</h2>
            {product.description && (
                <p className="text-muted-foreground mb-6">{product.description}</p>
            )}

            <div className="mb-6 flex-1">
                {product.contactOnly ? (
                    <div>
                        <div className="flex items-baseline gap-1">
                            <span className="text-3xl">
                                {t('pricing.custom')}
                            </span>
                        </div>
                        <div className="text-muted-foreground mt-2">
                            {t('pricing.customBillingPlan')}
                        </div>
                    </div>
                ) : pricingData?.pricingType === 'monthly_first_year' ? (
                    <>
                        <div className="flex items-baseline gap-1">
                                <span className="text-3xl">
                                    {getMonthlyFirstYearPrice()}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.period.firstYearMonthly')}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-1 mt-2">
                                <span className="text-xl">
                                    {getMonthlySubsequentYearPrice()}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.period.subsequentYearsMonthly')}
                                </span>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {t('pricing.period.billedAnnually')}
                            </div>
                        </>
                    ) : pricingData?.pricingType === 'monthly_6month' ? (
                        <>
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl">
                                    {getStudentMonthlyPrice()}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.period.sixMonthLicense')}
                                </span>
                            </div>
                        </>
                    ) : pricingData?.pricingType === 'package' ? (
                        <>
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl">
                                    {purchasePrice}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.period.packageLicenses', {count: pricingData.packageSize})}
                                </span>
                            </div>
                        </>
                    ) : pricingData?.pricingType === 'subscription_only' ? (
                        <>
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl">
                                    {subscriptionPrice}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.monthly')}
                                </span>
                            </div>
                            {isAnnual && (
                                <div className="text-sm text-muted-foreground mt-1">
                                    {t('pricing.billedAnnually')}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl">
                                    {purchasePrice}
                                </span>
                                <span className="text-muted-foreground">
                                    {t('pricing.period.onetime')}
                                </span>
                            </div>
                            {hasSubscription && (
                                <>
                                    <div className="text-sm text-muted-foreground mt-2">
                                        {t('pricing.subscription')}: {subscriptionPrice}
                                    </div>
                                    {isAnnual && (
                                        <div className="text-sm text-muted-foreground mt-1">
                                            {t('pricing.billedAnnually')}
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

            {!product.contactOnly && !product.hideQuantity && (
                <QuantitySelector
                    quantity={quantity}
                    onChange={onQuantityChange}
                    label={t('pricing.quantity')}
                />
            )}

            {product.contactOnly ? (
                <div className="space-y-2 mb-6 flex-0">
                    <Button
                        className="w-full rounded-full"
                        onClick={onContactClick}
                    >
                        <Mail className="mr-2 h-4 w-4"/>
                        {t('pricing.buttons.contactUs')}
                    </Button>
                </div>
            ) : (
                <div className="space-y-2 mb-6 flex-0">
                    {(product.showTrial !== false) && pricingData?.trialEligible === true && (
                        <Button
                            className={`w-full rounded-full ${
                                product.highlighted ? '' : 'bg-primary hover:bg-primary/90'
                            }`}
                            onClick={() => onTrialClick(product)}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                    {t('pricing.processing')}
                                </>
                            ) : (
                                <>
                                    <Play className="mr-2 h-4 w-4"/>
                                    {product.trialCta}
                                </>
                            )}
                        </Button>
                    )}
                    {(pricingData?.trialEligible === false) && product.showTrial !== false && (
                        <Button
                            variant="outline"
                            className="w-full rounded-full"
                            onClick={onContactClick}
                        >
                            <Mail className="mr-2 h-4 w-4"/>
                            {t('pricing.buttons.extendTrialContactUs')}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        className="w-full rounded-full"
                        onClick={() => onBuyClick(product)}
                        disabled={isLoading || ((product.key === 'education_student' || product.key === 'education') && hasEducationLicense)
                            || (product.key === 'designer_alumni' && pricingData?.hasValidLicense)}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                {t('pricing.processing')}
                            </>
                        ) : (
                            <>
                                <ShoppingBag className="mr-2 h-4 w-4"/>
                                {product.buyCta}
                            </>
                        )}
                    </Button>
                    {(product.key === 'education_student' || product.key === 'education') && hasEducationLicense && (
                        <div className="text-sm text-muted-foreground mt-2">
                            {t('pricing.studentEducation.alreadyOwned')}
                        </div>
                    )}
                    {product.key === 'designer_alumni' && pricingData?.hasValidLicense && (
                        <div className="text-sm text-muted-foreground mt-2">
                            {t('pricing.alumniAlreadyOwned')}
                        </div>
                    )}
                </div>
            )}

            <div className="border-t my-4"></div>
            <div className="space-y-3">
                {visibleFeatures.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"/>
                        <span className="text-muted-foreground">{feature}</span>
                    </div>
                ))}
                {hasMoreFeatures && (
                    <Button
                        variant="ghost"
                        className="px-0 h-auto"
                        onClick={() => setShowAllFeatures(prev => !prev)}
                    >
                        {showAllFeatures
                            ? t('pricing.features.showLess')
                            : t('pricing.features.showMore', {count: hiddenFeaturesCount})}
                    </Button>
                )}
            </div>
        </Card>
    );
}
